// src/ItmVrntPkgSuplrsPriceList.js ver 00a Jan 16 2026 11:41am - SB120c
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from './App';
import * as itmVrntPkgSuplrsPriceService from './api/itmVrntPkgSuplrsPriceService';
import * as itemVariantPackageService from './api/itemVariantPackageService'; // For filter dropdown
import * as supplierService from './api/supplierService'; // For filter dropdown
import PaginationBar2 from './PaginationBar2';

function ItmVrntPkgSuplrsPriceList() {
    // const { tenantId } = useParams();
    // const [prices, setPrices] = useState([]);
    const [itemVariantPackagesPrices, setItemVariantPackagesPrices] = useState([]); // NEW: Renamed state to reflect "prices"
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const auth = useAuth();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filter States
    const [supplierCodeFilter, setSupplierCodeFilter] = useState('no-filter');
    const [supplierCodeOptions, setSupplierCodeOptions] = useState([]);
    const [isSupplierCodeDropdownOpen, setIsSupplierCodeDropdownOpen] = useState(false);
    const supplierCodeDropdownRef = useRef(null);

    const [itmVrntPkgNameFilter, setItmVrntPkgNameFilter] = useState('no-filter');
    const [itmVrntPkgNameOptions, setItmVrntPkgNameOptions] = useState([]);
    const [isItmVrntPkgNameDropdownOpen, setIsItmVrntPkgNameDropdownOpen] = useState(false);
    const itmVrntPkgNameDropdownRef = useRef(null);

    const tenantId = auth.userProfile?.SubsCoDocId;

    useEffect(() => {
        const fetchPrices = async () => {
            if (!tenantId) {
                setError("Tenant ID missing.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication required. Please log in.");
                    setLoading(false);
                    return;
                }

                // Fetch filter options for ItemVariantPackage Name dropdown
                const itemVariantPackageResponse = await itemVariantPackageService.listActiveItemVariantPackageNames(tenantId, token);
                if (itemVariantPackageResponse && itemVariantPackageResponse.packages) {
                    setItmVrntPkgNameOptions(itemVariantPackageResponse.packages.map(pkg => ({
                        name: pkg.itemVariantPackageName,
                        id: pkg.id,
                    })));
                } else {
                    console.warn("Could not fetch active item variant packages for filter dropdown.");
                }

                // Fetch filter options for Supplier Code dropdown
                const suppliersResponse = await supplierService.getAllActiveSupplierCodesForDropdown(tenantId, token);
                if (suppliersResponse && suppliersResponse.suppliers) {
                    setSupplierCodeOptions(suppliersResponse.suppliers.map(sup => ({
                        code: sup.supplierCode,
                        name: sup.companyName,
                        id: sup.id,
                    })));
                } else {
                    console.warn("Could not fetch active supplier codes for filter dropdown.");
                }


                const response = await itmVrntPkgSuplrsPriceService.getItmVrntPkgSuplrsPrices(
                    tenantId,
                    token,
                    currentPage,
                    itemsPerPage,
                    supplierCodeFilter,
                    itmVrntPkgNameFilter
                );
                // setItemVariantPackages(response.prices); // Note: response contains 'prices'
                setItemVariantPackagesPrices(response.prices); // Assuming the response.prices contains the list data
                setTotalItems(response.totalItems);
                setTotalPages(response.totalPages);

                //close filter dropdown after response awaited
                setIsSupplierCodeDropdownOpen(false);
                setIsItmVrntPkgNameDropdownOpen(false);



            } catch (err) {
                console.error("Error fetching item variant package prices from Cloud Function: ", err);
                setError(`Failed to load item variant package prices: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchPrices();
    }, [auth, tenantId, currentPage, itemsPerPage, supplierCodeFilter, itmVrntPkgNameFilter]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleLimitChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1); // Reset to first page when limit changes
    };

    // Generic toggle for any dropdown
    const toggleDropdown = (setter) => {
        setter(prev => !prev);
    };

    // Filter change handlers
    const handleSupplierCodeFilterChange = (filter) => {
        setSupplierCodeFilter(filter);
        setCurrentPage(1);
        setIsSupplierCodeDropdownOpen(false);
    };

    const handleItmVrntPkgNameFilterChange = (filter) => {
        setItmVrntPkgNameFilter(filter);
        setCurrentPage(1);
        setIsItmVrntPkgNameDropdownOpen(false);
    };


    // Close all dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (supplierCodeDropdownRef.current && !supplierCodeDropdownRef.current.contains(event.target)) {
                setIsSupplierCodeDropdownOpen(false);
            }
            if (itmVrntPkgNameDropdownRef.current && !itmVrntPkgNameDropdownRef.current.contains(event.target)) {
                setIsItmVrntPkgNameDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    // Helper to get supplier company name from code for display in header
    const getSupplierCompanyNameFromCode = (code) => {
        const supplier = supplierCodeOptions.find(sup => sup.code === code);
        return supplier ? supplier.name : 'Unknown Supplier';
    };


    if (loading) return <p>Loading item variant package prices...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!tenantId) return <p style={{ color: 'orange' }}>Tenant ID is required to manage item variant package prices.</p>;


    return (
        <div>
            <div className="page-header">
                <h2>Item Variant Package Prices</h2>
                <Link to={`/itm-vrnt-pkg-suplrs-price/add`} className="btn secondary">Add New Price Record</Link>
            </div>

            <PaginationBar2
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />

            <table>
                <thead>
                    <tr>
                        <th
                            className="dropdown-th"
                            onClick={() => toggleDropdown(setIsSupplierCodeDropdownOpen)}
                            ref={supplierCodeDropdownRef}
                            title="Click to Filter"
                        >
                            SuplCd {supplierCodeFilter !== 'no-filter' && `(${getSupplierCompanyNameFromCode(supplierCodeFilter)})`}
                            {isSupplierCodeDropdownOpen && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => handleSupplierCodeFilterChange('no-filter')}>&lt;no filter&gt;</li>
                                    {supplierCodeOptions.map(sup => (
                                        <li key={sup.id} onClick={() => handleSupplierCodeFilterChange(sup.code)}>{sup.code} - {sup.name}</li>
                                    ))}
                                </ul>
                            )}
                        </th>
                        <th
                            className="dropdown-th"
                            onClick={() => toggleDropdown(setIsItmVrntPkgNameDropdownOpen)}
                            ref={itmVrntPkgNameDropdownRef}
                            title="Click to Filter"
                        >
                            ItmPkg {itmVrntPkgNameFilter !== 'no-filter' && `(${itmVrntPkgNameFilter})`}
                            {isItmVrntPkgNameDropdownOpen && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => handleItmVrntPkgNameFilterChange('no-filter')}>&lt;no filter&gt;</li>
                                    {itmVrntPkgNameOptions.map(pkg => (
                                        <li key={pkg.id} onClick={() => handleItmVrntPkgNameFilterChange(pkg.name)}>{pkg.name}</li>
                                    ))}
                                </ul>
                            )}
                        </th>
                        <th>Price</th>
                        <th>Prc/PkUOM</th>
                        <th>StdPc/UOM</th>
                        <th>ChfRtg</th>
                        <th>ConfirmDte</th>
                        <th>Date Entered</th>
                        <th>Date Last Modified</th>
                    </tr>
                </thead>
                <tbody>
                    {/* FIXED ver 0a: Use the correct state variable for mapping */}
                    {itemVariantPackagesPrices.map(price => (
                        <tr key={price.id}>
                            <td>{price.SupplierCode}</td>
                            <td>
                                <Link to={`/itm-vrnt-pkg-suplrs-price/details/${price.id}`}>
                                    {price.ItmVrntPkgName}
                                </Link>
                            </td>
                            <td>{price.Price.toFixed(2)}</td>
                            <td>{price.PkgPricePerPkgUOM.toFixed(4)}</td>
                            <td>{price.PkgPricePerItmUOM.toFixed(4)}</td>
                            <td>{price.ChefsRating}</td>
                            <td>{price.DateSamePriceConfirmed ? new Date(price.DateSamePriceConfirmed._seconds * 1000).toLocaleString() : 'N/A'}</td>
                            <td>{price.DateEntered ? new Date(price.DateEntered._seconds * 1000).toLocaleString() : 'N/A'}</td>
                            <td>{price.DateLastModified ? new Date(price.DateLastModified._seconds * 1000).toLocaleString() : 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <PaginationBar2
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />
        </div>
    );
}

export default ItmVrntPkgSuplrsPriceList;